import DOMPurify from "dompurify";
import { useEffect, useMemo, useRef } from "react";
import { useColorMode } from "../theme/ThemeProvider";
import type { Attachment } from "../api/threads";
import { apiUrl } from "../api/client";

const SANITIZE_OPTS = {
  USE_PROFILES: { html: true },
  FORBID_TAGS: ["style", "script", "iframe", "object", "embed", "form", "input", "link", "meta"],
  FORBID_ATTR: ["onerror", "onclick", "onload", "onmouseover"],
};

function rewriteCids(html: string, messageId: string, attachments: Attachment[]): string {
  const inline = attachments.filter((a) => a.inline && a.contentId);
  if (inline.length === 0) return html;
  return html.replace(/(["'])cid:([^"']+)\1/g, (whole, quote: string, cid: string) => {
    const att = inline.find((a) => a.contentId === cid);
    if (!att) return whole;
    return `${quote}${apiUrl(`/messages/${messageId}/attachments/${att.attachmentId}`)}${quote}`;
  });
}

function buildSrcdoc(rawHtml: string, dark: boolean): string {
  const sanitized = DOMPurify.sanitize(rawHtml, SANITIZE_OPTS);
  const bg = dark ? "#1A1B22" : "#FFFFFF";
  const fg = dark ? "#F5F6FA" : "#0F1115";
  const linkColor = dark ? "#7C8AFF" : "#5B6CFF";
  return `<!doctype html><html><head><meta charset="utf-8">
<base target="_blank" rel="noopener noreferrer">
<style>
  :root { color-scheme: ${dark ? "dark" : "light"}; }
  html, body { margin: 0; padding: 0; background: ${bg}; color: ${fg};
    font-family: Inter, -apple-system, BlinkMacSystemFont, "PingFang SC", "Noto Sans SC", sans-serif;
    font-size: 14px; line-height: 1.6; word-break: break-word; }
  body { padding: 4px 2px; }
  img, video, table { max-width: 100% !important; height: auto; }
  table { border-collapse: collapse; }
  a { color: ${linkColor}; }
  blockquote { border-left: 3px solid var(--ant-color-border, #ddd); margin: 0 0 0 8px; padding-left: 12px; color: ${dark ? "#9CA0B0" : "#5B6072"}; }
  pre, code { background: ${dark ? "#0E0F13" : "#FAFAFB"}; padding: 2px 6px; border-radius: 4px; font-size: 13px; }
  pre { padding: 10px; overflow: auto; }
</style></head><body>${sanitized}</body></html>`;
}

export function HtmlBody({
  html,
  messageId,
  attachments = [],
  maxHeight = 800,
}: {
  html: string;
  messageId?: string;
  attachments?: Attachment[];
  maxHeight?: number;
}) {
  const { mode } = useColorMode();
  const ref = useRef<HTMLIFrameElement | null>(null);
  const srcdoc = useMemo(() => {
    const withCids = messageId ? rewriteCids(html, messageId, attachments) : html;
    return buildSrcdoc(withCids, mode === "dark");
  }, [html, mode, messageId, attachments]);

  useEffect(() => {
    const iframe = ref.current;
    if (!iframe) return;
    const adjust = () => {
      try {
        const doc = iframe.contentDocument;
        if (!doc) return;
        const h = Math.min(maxHeight, Math.max(80, doc.documentElement.scrollHeight + 8));
        iframe.style.height = `${h}px`;
      } catch {
        // cross-origin (shouldn't happen with srcdoc, but be safe)
      }
    };
    iframe.addEventListener("load", adjust);
    const t = setTimeout(adjust, 50);
    const t2 = setTimeout(adjust, 300);
    return () => {
      iframe.removeEventListener("load", adjust);
      clearTimeout(t);
      clearTimeout(t2);
    };
  }, [srcdoc, maxHeight]);

  return (
    <iframe
      ref={ref}
      srcDoc={srcdoc}
      title="email-body"
      sandbox="allow-popups allow-popups-to-escape-sandbox allow-same-origin"
      style={{
        width: "100%",
        height: 80,
        border: "none",
        borderRadius: 8,
        background: "transparent",
        display: "block",
      }}
    />
  );
}
